import axios from "axios";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const AxiosContext = createContext();
export const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_DOMAIN}`,
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "Accept-Language": localStorage.getItem("locale") || "en",
  },
});

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error.response?.status === 401) {
      localStorage.removeItem("user_data");
      window.location.href = "/login";
    }
    return Promise.reject(error.response);
  }
);

const shouldShowLoader = (config) => {
  const method = config.method?.toLowerCase();
  if (method === "get") return false;
  return true;
};

const AxiosProvider = ({ children }) => {
  const [loader, setLoader] = useState(false);
  const counterRef = useRef(0);
  const navigate = useNavigate();

  const increment = useCallback(() => {
    counterRef.current += 1;
    setLoader(true);
  }, []);

  const decrement = useCallback(() => {
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    const reqId = axiosInstance.interceptors.request.use((config) => {
      if (shouldShowLoader(config)) {
        increment();
      }
      return config;
    });

    const resId = axiosInstance.interceptors.response.use(
      (res) => {
        if (shouldShowLoader(res.config)) {
          decrement();
        }
        return res;
      },
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("user_data");
          window.location.href = "/login";
        }
        if (shouldShowLoader(err?.config)) {
          decrement();
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(reqId);
      axiosInstance.interceptors.response.eject(resId);
    };
  }, [increment, decrement]);

  return (
    <AxiosContext.Provider value={{ loader, setLoader }}>
      {children}
    </AxiosContext.Provider>
  );
};

export default AxiosProvider;
